import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { BaseStyleEnums, CoreUtils, Select, SelectTypes, CoreCommonMessages, CustomIcons } from '@nhl/nhl-breakaway-ui';

import commonMessages from '~/core/commonMessages';
import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';
import { PeriodDescriptor } from '~/core/types';
import { parseValueFromLocalizedAttribute } from '~/core/utils';
import { Play as PlayType, PlayByPlayGame } from '~/views/pages/gameCenter/types';

import Play from './components/play/Play';
import Stoppage from './components/stoppage/Stoppage';
import { RosterMap } from './types';
import { ALL_OPTIONS, findMatchingPlayer, GAME_STOPPED_EVENTS, getUniqueValues } from './utils';

import {
    AllPlaysContainer,
    FilterContainer,
    NoPlaysMessage,
    PlayByPlayContainer,
    PlayByPlayHeader,
    SelectContainer,
    SelectLabel,
    ToggleFiltersContainer,
} from './styles';

type Props = {
    game: PlayByPlayGame;
};

const PlayByPlayDetails: FC<Props> = ({ game }) => {
    const intl = useIntl();

    const ALL_PLAYS = useMemo(
        () => ({
            key: 'all',
            label: intl.formatMessage({
                description: 'A word describing an option to show all data and not to filter',
                defaultMessage: 'All',
            }),
            value: ALL_OPTIONS,
        }),
        [intl]
    );
    const size = useSizeWithThrottle();
    const isMedium = size.width >= BaseStyleEnums.ScreenWidthBreakpoints.medium;

    // Period and Play
    const [selectedPeriod, setSelectedPeriod] = useState<SelectTypes.BasicOption>();
    const [periodOptions, setPeriodOptions] = useState<Array<SelectTypes.BasicOption>>([]);

    const [selectedPlay, setSelectedPlay] = useState<SelectTypes.BasicOption>();
    const [playOptions, setPlayOptions] = useState<Array<SelectTypes.BasicOption>>([]);

    // Away and Home team players
    const [selectedAwayPlayer, setSelectedAwayPlayer] = useState<SelectTypes.BasicOption>();
    const [selectedHomePlayer, setSelectedHomePlayer] = useState<SelectTypes.BasicOption>();
    const [awayPlayerOptions, setAwayPlayerOptions] = useState<Array<SelectTypes.BasicOption>>([]);
    const [homePlayerOptions, setHomePlayerOptions] = useState<Array<SelectTypes.BasicOption>>([]);
    const [isDisplayingFilters, setIsDisplayingFilters] = useState<boolean>(false);

    const filteredPlays = useMemo(() => {
        if (game.plays) {
            const isPlayVisible = (play: PlayType): boolean => {
                const isMatchingPeriod =
                    selectedPeriod?.value === ALL_OPTIONS || selectedPeriod?.value === play.periodDescriptor.number;
                const isMatchingPlayType =
                    selectedPlay?.value === ALL_OPTIONS || selectedPlay?.value === play.typeDescKey;
                const isMatchingAwayPlayer =
                    selectedAwayPlayer?.value === ALL_OPTIONS ||
                    findMatchingPlayer(play, selectedAwayPlayer?.value as number);
                const isMatchingHomePlayer =
                    selectedHomePlayer?.value === ALL_OPTIONS ||
                    findMatchingPlayer(play, selectedHomePlayer?.value as number);

                return isMatchingPeriod && isMatchingPlayType && isMatchingAwayPlayer && isMatchingHomePlayer;
            };

            return (
                // TODO should be able to remove the sort once the data is already sorted
                // shows reverse-chronologically if game is live, shows chronologically otherwise
                game.plays
                    .filter(isPlayVisible)
                    .sort((a, b) =>
                        CoreUtils.isGameLive(game.gameState) ? b.sortOrder - a.sortOrder : a.sortOrder - b.sortOrder
                    )
            );
        }
        return [];
    }, [game.plays, selectedPeriod, selectedPlay, selectedAwayPlayer, selectedHomePlayer, game.gameState]);

    const rosterMap = useMemo(() => {
        const playerIdToRosterSpot: RosterMap = {};
        game.rosterSpots &&
            game.rosterSpots.forEach((rosterSpot) => {
                playerIdToRosterSpot[rosterSpot.playerId] = rosterSpot;
            });
        return playerIdToRosterSpot;
    }, [game?.rosterSpots]);

    const ariaLabels = useMemo(
        () => ({
            awayTeamPlayerSelect: {
                dropdownButton: intl.formatMessage({
                    defaultMessage: 'Expand Away Team Player Options',
                    description:
                        'Label for select element button, prompting user to expand away team player options in a dropdown menu',
                }),
                optionsBox: intl.formatMessage({
                    defaultMessage: 'Away Team Player Options',
                    description:
                        'Label indicating that user is viewing a list of away team player options for a select component',
                }),
            },
            homeTeamPlayerSelect: {
                dropdownButton: intl.formatMessage({
                    defaultMessage: 'Expand Home Team Player Options',
                    description:
                        'Label for select element button, prompting user to expand home team player options in a dropdown menu',
                }),
                optionsBox: intl.formatMessage({
                    defaultMessage: 'Home Team Player Options',
                    description:
                        'Label indicating that user is viewing a list of home team player options for a select component',
                }),
            },
            periodSelect: {
                dropdownButton: intl.formatMessage({
                    defaultMessage: 'Expand Game Period Options',
                    description:
                        'Label for select element button, prompting user to expand game period options in a dropdown menu',
                }),
                optionsBox: intl.formatMessage({
                    defaultMessage: 'Game Period Options',
                    description:
                        'Label indicating that user is viewing a list of game period options for a select component',
                }),
            },
            playTypeSelect: {
                dropdownButton: intl.formatMessage({
                    defaultMessage: 'Expand Play Type Options',
                    description:
                        'Label for select element button, prompting user to expand play type options (ex. Hits, Faceoff, Goal), in a dropdown menu',
                }),
                optionsBox: intl.formatMessage({
                    defaultMessage: 'Play Type Options',
                    description:
                        'Label indicating that user is viewing a list of play type options for a select component',
                }),
            },
        }),
        [intl]
    );

    const handleSelectUpdated =
        (setOption: (option: SelectTypes.BasicOption) => void) => (_e: unknown, option: SelectTypes.BasicOption) => {
            setOption(option);
        };

    /**
     * Handle period options
     */
    useEffect(() => {
        if (game.plays) {
            const allPeriods: { [key: number]: PeriodDescriptor } = {};
            game.plays.forEach(({ periodDescriptor }) => {
                allPeriods[periodDescriptor.number] = periodDescriptor;
            });

            const periodOptions: Array<SelectTypes.BasicOption> = Object.values(allPeriods).map((period) => ({
                key: period.number,
                label: CoreUtils.getPeriodDisplayFromDescriptor(intl, period, game.gameType),
                value: period.number,
            }));

            periodOptions.unshift(ALL_PLAYS);

            setPeriodOptions((po) => (po.length === periodOptions.length ? po : periodOptions));

            if (!selectedPeriod && periodOptions.length > 0) {
                const firstPeriod = periodOptions[0];
                const lastPeriod = periodOptions[periodOptions.length - 1];
                setSelectedPeriod(CoreUtils.isGameLive(game.gameState) ? lastPeriod : firstPeriod);
            }
        }
    }, [ALL_PLAYS, game.gameState, game.gameType, game.plays, intl, selectedPeriod]);

    const periodOptionsSelect = useMemo(
        () => (
            <Select
                dropdownButtonAriaLabel={ariaLabels.periodSelect.dropdownButton}
                id="period-select"
                options={periodOptions}
                selectedOption={periodOptions.find((periodOption) => periodOption.key === selectedPeriod?.key) || null}
                onOptionChange={handleSelectUpdated(setSelectedPeriod)}
                optionsBoxAriaLabel={ariaLabels.periodSelect.optionsBox}
            />
        ),
        [ariaLabels, periodOptions, selectedPeriod]
    );

    /**
     * Handle play type options
     */
    useEffect(() => {
        if (game.plays) {
            let periodPlays: Array<PlayType>;

            if (selectedPeriod?.value === ALL_PLAYS.value) {
                periodPlays = game.plays;
            } else {
                periodPlays = game.plays.filter((play) => play.periodDescriptor.number === selectedPeriod?.value);
            }

            const plays: string[] = getUniqueValues(periodPlays, 'typeDescKey');
            const playOptions: Array<SelectTypes.BasicOption> = plays.sort().map((playOption) => ({
                key: playOption,
                label: CoreUtils.safeFormatMessage(intl, commonMessages.gameEventTypeDescriptions[playOption]),
                value: playOption,
            }));

            playOptions.unshift(ALL_PLAYS);

            setPlayOptions((po) => (po.length === playOptions.length ? po : playOptions));
        }
    }, [ALL_PLAYS, game.plays, intl, selectedPeriod?.value]);

    const playOptionsSelect = useMemo(
        () => (
            <Select
                dropdownButtonAriaLabel={ariaLabels.playTypeSelect.dropdownButton}
                id="play-type-select"
                options={playOptions}
                selectedOption={playOptions.find((playOption) => playOption.key === selectedPlay?.key) || null}
                onOptionChange={(_, opt: SelectTypes.BasicOption) => {
                    setSelectedPlay(opt);
                }}
                optionsBoxAriaLabel={ariaLabels.playTypeSelect.dropdownButton}
            />
        ),
        [ariaLabels, playOptions, selectedPlay]
    );

    useEffect(() => {
        const isValidPeriodPlay = playOptions.find((option) => option.value === selectedPlay?.value);

        if (playOptions.length > 0 && (!selectedPlay || !isValidPeriodPlay)) {
            setSelectedPlay(playOptions[0]);
        }
    }, [playOptions, selectedPlay]);

    /**
     * Handle player options
     */
    const createPlayerOptions = useCallback(
        (teamId: number) => {
            if (game.rosterSpots) {
                const playerOptions: Array<SelectTypes.BasicOption> = game.rosterSpots
                    .filter((r) => r.teamId === teamId)
                    .map((roster) => ({
                        key: roster.playerId,
                        label: `${parseValueFromLocalizedAttribute(
                            roster.firstName
                        )} ${parseValueFromLocalizedAttribute(roster.lastName)}`,
                        value: roster.playerId,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label));
                playerOptions.unshift(ALL_PLAYS);

                return playerOptions;
            }
            return [];
        },
        [ALL_PLAYS, game.rosterSpots]
    );

    useEffect(() => {
        const awayTeamPlayerOptions = createPlayerOptions(game.awayTeam.id);
        const homeTeamPlayerOptions = createPlayerOptions(game.homeTeam.id);

        setAwayPlayerOptions((ao) => (ao.length === awayTeamPlayerOptions.length ? ao : awayTeamPlayerOptions));
        setHomePlayerOptions((ho) => (ho.length === homeTeamPlayerOptions.length ? ho : homeTeamPlayerOptions));
    }, [createPlayerOptions, game.awayTeam, game.homeTeam, selectedAwayPlayer, selectedHomePlayer]);

    useEffect(() => {
        if (!selectedAwayPlayer && awayPlayerOptions.length > 0) {
            setSelectedAwayPlayer(awayPlayerOptions[0]);
        }
        if (!selectedHomePlayer && homePlayerOptions.length > 0) {
            setSelectedHomePlayer(homePlayerOptions[0]);
        }
    }, [selectedAwayPlayer, selectedHomePlayer, awayPlayerOptions, homePlayerOptions]);

    const awayOptionsSelect = useMemo(
        () => (
            <Select
                dropdownButtonAriaLabel={ariaLabels.awayTeamPlayerSelect.dropdownButton}
                id="away-team-player-select"
                options={awayPlayerOptions}
                selectedOption={selectedAwayPlayer || null}
                onOptionChange={handleSelectUpdated(setSelectedAwayPlayer)}
                optionsBoxAriaLabel={ariaLabels.awayTeamPlayerSelect.optionsBox}
            />
        ),
        [ariaLabels, awayPlayerOptions, selectedAwayPlayer]
    );

    const homeOptionsSelect = useMemo(
        () => (
            <Select
                dropdownButtonAriaLabel={ariaLabels.homeTeamPlayerSelect.dropdownButton}
                id="home-team-player-select"
                options={homePlayerOptions}
                selectedOption={selectedHomePlayer || null}
                onOptionChange={handleSelectUpdated(setSelectedHomePlayer)}
                optionsBoxAriaLabel={ariaLabels.homeTeamPlayerSelect.optionsBox}
            />
        ),
        [ariaLabels, homePlayerOptions, selectedHomePlayer]
    );

    return (
        <PlayByPlayContainer>
            <PlayByPlayHeader role="heading" aria-level={2}>
                <FormattedMessage defaultMessage="Plays" description="A word that describes the plays in a game" />
            </PlayByPlayHeader>
            {(isMedium || isDisplayingFilters) && game.plays && game.plays.length > 0 && (
                <FilterContainer>
                    <SelectContainer>
                        <SelectLabel htmlFor="period-select">
                            <FormattedMessage {...CoreCommonMessages.gamePeriodDescriptions.long.periodText} />
                        </SelectLabel>
                        {periodOptionsSelect}
                    </SelectContainer>

                    <SelectContainer>
                        <SelectLabel htmlFor="play-type-select">
                            <FormattedMessage
                                defaultMessage="Play Type"
                                description="A phrase that describes different types of hockey plays in a game. Ex. Hits, Faceoff, Goal"
                            />
                        </SelectLabel>
                        {playOptionsSelect}
                    </SelectContainer>

                    <SelectContainer>
                        <SelectLabel htmlFor="away-team-player-select">
                            {parseValueFromLocalizedAttribute(game.awayTeam.commonName)}
                        </SelectLabel>
                        {awayOptionsSelect}
                    </SelectContainer>
                    <SelectContainer>
                        <SelectLabel htmlFor="home-team-player-select">
                            {parseValueFromLocalizedAttribute(game.homeTeam.commonName)}
                        </SelectLabel>
                        {homeOptionsSelect}
                    </SelectContainer>
                </FilterContainer>
            )}
            {!isMedium && (
                <ToggleFiltersContainer onClick={() => setIsDisplayingFilters(!isDisplayingFilters)}>
                    <FormattedMessage
                        {...commonMessages.misc.callToAction[isDisplayingFilters ? 'hideFilters' : 'showFilters']}
                    />
                </ToggleFiltersContainer>
            )}
            {game.plays && game.plays.length > 0 ? (
                <AllPlaysContainer>
                    {filteredPlays.map((play) =>
                        GAME_STOPPED_EVENTS.includes(play.typeCode) ? (
                            <Stoppage key={play.eventId} play={play} />
                        ) : (
                            <Play key={play.eventId} game={game} rosterMap={rosterMap} play={play} />
                        )
                    )}
                </AllPlaysContainer>
            ) : (
                <NoPlaysMessage>
                    <CustomIcons.ZamboniIcon />
                    <FormattedMessage
                        description="A phrase describing that no plays exist yet for a given game"
                        defaultMessage="No plays exist for this game yet"
                    />
                </NoPlaysMessage>
            )}
        </PlayByPlayContainer>
    );
};

export default PlayByPlayDetails;
